import {
    Box,
    Center,
    Container,
    Flex,
    Image,
    Input,
    Text,
    useBoolean,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { serviceAreaAry } from "assets/data/servicearea";
import DaumPostCodeModal from "components/DaumPostCodeModal";
import OneButtonModal from "components/OneButtonModal";
import ScheduleRecordModal from "components/ScheduleRecordModal";
import { ResStatus } from "lib/api/enum";
import usePathNavigate from "lib/hook/usePathNavigate";
import { onRegexValidationPhone } from "lib/utils/regular";
import { ChangeEvent, useEffect, useState } from "react";
import { Address } from "react-daum-postcode";
import { useParams } from "react-router-dom";
import { callOrderDetailApi } from "routes/Order/api";
import OrderInfo from "routes/Order/components/OrderInfo";
import ShippingInput from "routes/Order/components/ShippingInput";
import ReservationInfo from "../Order/components/ReservationInfo";
import { callPickupReservationApi } from "./api";

interface ScheduleData {
    isFace: boolean;
    date: string;
}

const PickupReservation = () => {
    const params = useParams();
    const { pathNavigate } = usePathNavigate();
    const toast = useToast();

    const {
        isOpen: modalIsOpen,
        onClose: modalClose,
        onOpen: modalOpen,
    } = useDisclosure();

    const [loaded, setLoaded] = useState(false);
    const [orderId, setOrderId] = useState(0);
    const [regDate, setRegDate] = useState(0);
    // 이름
    const [name, setName] = useState<string>("");
    const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    // 전화번호
    const [phone, setPhone] = useState<string>("");
    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const phoneNum = onRegexValidationPhone(e.target.value);
        setPhone(phoneNum);
    };

    // 주소
    const [addressData, setAddressData] = useState({
        zonecode: "",
        roadAddress: "",
        sido: "",
        sigungu: "",
        sigunguCode: "",
        detailAddress: "",
    });
    const [activePost, setActivePost] = useBoolean(false);
    const [isArea, setIsArea] = useState<boolean>();

    const callbackPostCode = (data?: Address) => {
        setActivePost.off();
        if (data === null || data === undefined) return;

        setAddressData({
            zonecode: data.zonecode,
            roadAddress: data.roadAddress,
            sido: data.sido,
            sigungu: data.sigungu,
            sigunguCode: data.sigunguCode,
            detailAddress: "",
        });
        const areaAry: string[] = serviceAreaAry() as string[];
        const area = areaAry.some((code) => code === data.sigunguCode);
        setIsArea(area);
    };
    const onChangeDetailAddress = (e: ChangeEvent<HTMLInputElement>) => {
        setAddressData((prevState) => ({
            ...prevState,
            detailAddress: e.target.value,
        }));
    };

    const [scheduleData, setScheduleData] = useState<ScheduleData>();
    const [activeSchedule, setActiveSchedule] = useBoolean(false);
    const [comments, setComments] = useState("");

    const onClickReservation = () => {
        setActiveSchedule.toggle();
    };

    const onChangeComments = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setComments(e.target.value);
    };

    const callbackSchedule = (data?: ScheduleData) => {
        console.log("callbackSchedule : ", data);

        setActiveSchedule.off();
        if (data == null || data == undefined) return;

        setScheduleData({
            isFace: data.isFace,
            date: data.date,
        });
    };

    const [isActiveBtn, setIsActiveBtn] = useBoolean(false);
    const checkActiveBtn = () => {
        setIsActiveBtn.off();
        if (name.length < 2) return;
        if (phone == null || phone.toString().length <= 8) return;
        if (!addressData?.zonecode) return;

        setIsActiveBtn.on();
    };

    const { mutate: pickupInfoMutate } = useMutation(callOrderDetailApi, {
        onSuccess: (data) => {
            console.log("pickupInfoMutate : ", data);
            if (data.status === ResStatus.RES_SUCCESS) {
                const {
                    order_id,
                    // merchant_uid,
                    order_status,
                    // delivery_method,
                    // mint_complete_date,
                    reg_date,
                    // item_list,
                    pickup,
                    // delivery,
                    // payment,
                } = data.command;
                setRegDate(reg_date);

                // 결제완료1,취소2,픽업중3,픽업완료4,민트시작5,민트종료6,배송중7,배송완료8,민트거절9
                if (order_status === 1) {
                    if (pickup) {
                        setOrderId(order_id);
                        setName(pickup.address_name);
                        setPhone(pickup.address_phone);
                        setAddressData({
                            zonecode: pickup.post_num,
                            roadAddress: pickup.address,
                            sido: pickup.sido,
                            sigungu: pickup.sigungu,
                            sigunguCode: pickup.sigungu_code,
                            detailAddress: pickup.address_detail,
                        });
                        if (pickup.reservation_date != null) {
                            setScheduleData({
                                isFace: pickup.untact_yn === 0,
                                date: pickup.reservation_date.toString(),
                            });
                        }
                        setComments(pickup.comment);
                        setLoaded(true);
                    } else {
                        toast({
                            title: "오류",
                            description: "none pickup",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                            position: "top",
                        });
                    }
                } else {
                    pathNavigate("/external_sign/reservation/completed");
                }
            } else {
                toast({
                    title: "오류",
                    description: data.result_message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                });
            }
        },
        onError: (error: any) => {
            console.log("onError : ", error);
            toast({
                title: "오류",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
        },
    });

    const { mutate: pickupReservationMutate } = useMutation(
        callPickupReservationApi,
        {
            onSuccess: (data) => {
                console.log("onSuccess : ", data);
                if (data.status === ResStatus.RES_SUCCESS) {
                    // 저장 성공
                    modalOpen();
                } else {
                    toast({
                        title: "오류",
                        description: data.result_message,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                        position: "top",
                    });
                }
            },
            onError: (error: any) => {
                console.log("onError : ", error);
                toast({
                    title: "오류",
                    description: error.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                });
            },
        }
    );

    const onClickSave = () => {
        pickupReservationMutate({
            order_id: orderId,
            reservation_date: Number.parseInt(scheduleData?.date!),
            address_name: name,
            address_phone: phone, // 전화번호
            address: addressData.roadAddress,
            address_detail: addressData.detailAddress,
            post_num: addressData.zonecode,
            sido: addressData.sido,
            sigungu: addressData.sigungu,
            sigungu_code: addressData.sigunguCode,
            comment: comments,
            comment_save: 0,
            untact_yn: scheduleData?.isFace ? 0 : 1,
        });
    };

    const onClickModal = () => {
        pathNavigate("/external_sign/reservation/completed");
    };

    useEffect(() => {
        if (params.order_id) {
            pickupInfoMutate({
                order_id: Number.parseInt(params.order_id.toString()),
            });
        }
    }, []);

    useEffect(() => {
        checkActiveBtn();
    }, [name, phone, addressData, scheduleData]);

    return loaded ? (
        <Box>
            <Center
                w="100%"
                h="4rem"
                bgColor={"white"}
                fontSize="1.25rem"
                fontWeight={"semibold"}
                zIndex="1"
            >
                픽업일시 예약
            </Center>
            <OrderInfo
                reservationDate={regDate}
                phone={phone}
                orderId={orderId}
            />

            <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />
            <Box mx="20px">
                <ShippingInput
                    title="픽업 정보"
                    name={name}
                    phone={phone}
                    addressData={addressData}
                    callBackName={onChangeName}
                    callBackPhone={onChangePhone}
                    callBackSearchPostCode={setActivePost.toggle}
                    callBackDetailAddress={onChangeDetailAddress}
                />
            </Box>
            <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />
            <Box mx="20px">
                <ReservationInfo
                    title="예약일시"
                    reservationDate={scheduleData?.date!}
                    isFace={scheduleData?.isFace!}
                    comments={comments}
                    callbackReservation={onClickReservation}
                    callbackOnChangeComments={onChangeComments}
                />
            </Box>

            <Container
                bottom={0}
                bgColor="#fff"
                maxW={"768px"}
                minW={"320px"}
                w="100%"
                h="86px"
                px={"20px"}
                py="15px"
                borderTop={"1px"}
                borderTopColor={"#e0e0e0"}
            >
                <Input
                    w="100%"
                    h={"100%"}
                    fontSize={"1rem"}
                    fontWeight="semibold"
                    bg={isActiveBtn ? "#5bbca6" : "#e0e0e0"}
                    color={"#fff"}
                    type="submit"
                    onClick={onClickSave}
                    value={`저장하기`}
                />
            </Container>
            <DaumPostCodeModal
                isOpen={activePost}
                callback={callbackPostCode}
            />

            <ScheduleRecordModal
                reservationType="pickup"
                data={scheduleData}
                isOpen={activeSchedule}
                callback={callbackSchedule}
            />
            <OneButtonModal
                isOpen={modalIsOpen}
                buttonText="확인"
                onClose={modalClose}
                onClickButton={onClickModal}
            >
                <Flex
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems="center"
                >
                    <Image
                        src="/assets/images/icn_circle_check.svg"
                        mt="20px"
                        boxSize={"56px"}
                    />
                    <Text mt="14px" fontSize={"15px"} fontWeight="semibold">
                        버틀러 예약이 완료되었습니다.
                    </Text>
                </Flex>
            </OneButtonModal>
        </Box>
    ) : (
        <></>
    );
};

export default PickupReservation;
